// client/components/widgets/WidgetLayout.jsx
// ✅ Widget Layout - Koordiniert linke und rechte Rail um den Content
import React, { memo, useState, useCallback } from "react";
import { useViewportTier, VIEWPORT_TIERS } from "../../hooks/widgets/index.js";
import { useWidgetConfig } from "../../hooks/widgets/index.js";
import { tw, cn } from "../../utils/themeClasses.js";
import WidgetRail from "./WidgetRail.jsx";
import WidgetAddModal from "./WidgetAddModal.jsx";
import { getWidgetById } from "./registry.js";
import { GripVertical } from "lucide-react";

// dnd-kit für Cross-Rail Drag & Drop
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay, pointerWithin, rectIntersection } from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";

// Konstanten
const RAIL_WIDTH = 256; // Schmale Rails = mehr Platz für Inhalt
const MAX_WIDGETS_PER_RAIL = 12; // Mit Scrolling kein Problem mehr

/**
 * Drag Overlay Komponente - Zeigt Preview beim Ziehen
 */
const DragPreview = memo(function DragPreview({
  widgetId
}) {
    const widget = getWidgetById(widgetId);
  if (!widget) return null;
  const {
    name,
    icon: Icon
  } = widget;
  return <div className={cn("rounded-2xl border shadow-md px-2.5 py-1.5", "flex items-center gap-1.5 cursor-grabbing", tw.card(true), "ring-2 ring-blue-500/50")} style={{
    width: "232px"
  }}>
      <GripVertical size={12} className={cn(tw.textMuted(true))} />
      {Icon && <Icon size={13} className="text-blue-400" />}
      <span className={cn("text-[12px] font-semibold truncate", tw.textSecondary(true))}>
        {name}
      </span>
    </div>;
});

/**
 * WidgetLayout - Wraps Dashboard content with optional Widget Rails
 */
const WidgetLayout = memo(function WidgetLayout({
  children
}) {
  const {
    tier
  } = useViewportTier();
  const {
    availableWidgets,
    leftWidgets,
    rightWidgets,
    addWidget,
    removeWidget,
    toggleMinimize,
    reorderWidgets
  } = useWidgetConfig();

  // Modal State
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalPosition, setAddModalPosition] = useState("left");

  // Drag State
  const [activeId, setActiveId] = useState(null);

  // Handler
  const handleAddWidget = useCallback(position => {
    setAddModalPosition(position);
    setAddModalOpen(true);
  }, []);
  const handleAddConfirm = useCallback(async (widgetId, position) => {
    await addWidget(widgetId, position);
    setAddModalOpen(false);
  }, [addWidget]);
  const handleRemoveWidget = useCallback(async widgetId => {
    await removeWidget(widgetId);
  }, [removeWidget]);
  const handleToggleMinimize = useCallback(async widgetId => {
    await toggleMinimize(widgetId);
  }, [toggleMinimize]);
  const handleReorder = useCallback(async (sourceRail, sourceIndex, targetRail, targetIndex) => {
    await reorderWidgets(sourceRail, sourceIndex, targetRail, targetIndex);
  }, [reorderWidgets]);

  // dnd-kit Sensoren für Cross-Rail Drag & Drop
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 10 // Etwas mehr Distanz für bessere UX
    }
  }), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));

  // Drag Start Handler
  const handleDragStart = useCallback(event => {
    setActiveId(event.active.id);
    // Body-Klasse für globales Cursor-Styling
    document.body.classList.add("dragging-widget");
  }, []);

  // Drag Cancel Handler
  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    document.body.classList.remove("dragging-widget");
  }, []);

  // Cross-Rail Drag End Handler
  const handleDragEnd = useCallback(event => {
    const {
      active,
      over
    } = event;

    // Cleanup
    setActiveId(null);
    document.body.classList.remove("dragging-widget");
    if (!over) return;
    const draggedId = active.id;
    const overId = over.id;

    // Finde Source-Rail und Index
    let sourceRail = null;
    let sourceIndex = -1;
    const leftIdx = leftWidgets.findIndex(w => w.id === draggedId);
    const rightIdx = rightWidgets.findIndex(w => w.id === draggedId);
    if (leftIdx !== -1) {
      sourceRail = "left";
      sourceIndex = leftIdx;
    } else if (rightIdx !== -1) {
      sourceRail = "right";
      sourceIndex = rightIdx;
    }
    if (!sourceRail) return;

    // Finde Target-Rail und Index
    let targetRail = null;
    let targetIndex = -1;

    // Prüfe ob über einem Widget gedroppt
    const leftTargetIdx = leftWidgets.findIndex(w => w.id === overId);
    const rightTargetIdx = rightWidgets.findIndex(w => w.id === overId);
    if (leftTargetIdx !== -1) {
      targetRail = "left";
      targetIndex = leftTargetIdx;
    } else if (rightTargetIdx !== -1) {
      targetRail = "right";
      targetIndex = rightTargetIdx;
    } else if (overId === "droppable-left") {
      // Auf leere linke Rail gedroppt
      targetRail = "left";
      targetIndex = leftWidgets.length;
    } else if (overId === "droppable-right") {
      // Auf leere rechte Rail gedroppt
      targetRail = "right";
      targetIndex = rightWidgets.length;
    }
    if (!targetRail) return;

    // Nur wenn sich was geändert hat
    if (sourceRail === targetRail && sourceIndex === targetIndex) return;
    handleReorder(sourceRail, sourceIndex, targetRail, targetIndex);
  }, [leftWidgets, rightWidgets, handleReorder]);
  const canShowBoth = tier === VIEWPORT_TIERS.DUAL;
  const hasLeftWidgets = leftWidgets.length > 0;
  const hasRightWidgets = rightWidgets.length > 0;

  // Zeige Rails nur mit genügend Platz; bei NONE bleibt der Content stabil gewrappt.
  const showLeft = tier !== VIEWPORT_TIERS.NONE && (hasLeftWidgets || canShowBoth);
  const showRight = tier !== VIEWPORT_TIERS.NONE && (hasRightWidgets || canShowBoth);
  return <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
        <div className="flex w-full min-h-screen justify-center">
          {/* Linke Rail */}
          {showLeft && <aside className="flex-shrink-0 sticky top-0 self-start max-h-screen overflow-y-auto overflow-x-hidden pt-4 pb-3 px-1.5 scrollbar-thin" style={{
          width: `${RAIL_WIDTH}px`
        }}>
              <WidgetRail position="left" widgets={leftWidgets} width={RAIL_WIDTH - 12} maxWidgets={MAX_WIDGETS_PER_RAIL} onAddWidget={handleAddWidget} onRemoveWidget={handleRemoveWidget} onToggleMinimize={handleToggleMinimize} isDragging={!!activeId} />
            </aside>}

          {/* Main Content - fixe max-width, zentriert */}
          <main className="flex-1 min-w-0 max-w-5xl">
            {children}
          </main>

          {/* Rechte Rail */}
          {showRight && <aside className="flex-shrink-0 sticky top-0 self-start max-h-screen overflow-y-auto overflow-x-hidden pt-4 pb-3 px-1.5 scrollbar-thin" style={{
          width: `${RAIL_WIDTH}px`
        }}>
              <WidgetRail position="right" widgets={rightWidgets} width={RAIL_WIDTH - 12} maxWidgets={MAX_WIDGETS_PER_RAIL} onAddWidget={handleAddWidget} onRemoveWidget={handleRemoveWidget} onToggleMinimize={handleToggleMinimize} isDragging={!!activeId} />
            </aside>}
        </div>

        {/* Drag Overlay - Zeigt Preview beim Ziehen */}
        <DragOverlay dropAnimation={{
        duration: 200,
        easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)"
      }}>
          {activeId ? <DragPreview widgetId={activeId} /> : null}
        </DragOverlay>
      </DndContext>

      {/* Add Widget Modal - User wählt Position */}
      <WidgetAddModal isOpen={addModalOpen} onClose={() => setAddModalOpen(false)} onAdd={handleAddConfirm} defaultPosition={addModalPosition} existingWidgetIds={[...leftWidgets.map(w => w.id), ...rightWidgets.map(w => w.id)]} availableWidgets={availableWidgets} leftCount={leftWidgets.length} rightCount={rightWidgets.length} maxPerRail={MAX_WIDGETS_PER_RAIL} />
    </>;
});
export default WidgetLayout;